import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { addSubscriber } from '../../services/api';
import { isValidEmail } from '../../utils/validators';
import { trackSubscription } from '../../utils/analytics';
import { getUserLocationInfo, getBrowserInfo } from '../../utils/locationInfo';
import Button from '../common/Button';
import { debug } from '../../utils/debug';

interface SubscriptionFormProps {
  buttonText?: string;
  priority?: 'primary' | 'secondary';
}

const SubscriptionForm: React.FC<SubscriptionFormProps> = ({ buttonText, priority = 'primary' }) => {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const { t, i18n } = useTranslation();
  
  // Client-side validation before form submission
  const validateEmail = (email: string): boolean => {
    const trimmedEmail = email.trim();
    
    if (!trimmedEmail) {
      setStatus('error');
      setErrorMessage('Please enter an email address');
      return false;
    }
    
    if (!isValidEmail(trimmedEmail)) {
      setStatus('error');
      setErrorMessage('Please enter a valid email address');
      return false;
    }
    
    // Update the email state with the trimmed value
    setEmail(trimmedEmail);
    
    return true;
  };
  
  // Handle input change with real-time validation
  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newEmail = e.target.value;
    setEmail(newEmail);
    
    // Clear error when user starts typing again
    if (status === 'error') {
      setStatus('idle');
      setErrorMessage('');
    }
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Reset state
    setErrorMessage('');
    
    // Validate email format
    if (!validateEmail(email)) {
      return;
    }
    
    setStatus('submitting');
    
    try {
      // Get browser information
      const browserInfo = getBrowserInfo();
      debug.log('Browser info retrieved', browserInfo);
      
      // Get location information with better error handling
      let locationInfo = {};
      try {
        debug.log('Attempting to get location info');
        locationInfo = await getUserLocationInfo();
        debug.log('Location info retrieved successfully', locationInfo);
      } catch (locError) {
        debug.error('Error getting location info', locError);
        // Continue with subscription even if location info fails
        // We'll handle this in the addSubscriber function with default values
      }
      
      // Prepare metadata with all required fields
      const metadata = {
        // Browser and device info
        ...browserInfo,
        // Location info (might be empty)
        ...locationInfo,
        // Source tracking
        source: window.location.href,
        referrer: document.referrer,
        // UTM parameters from URL if present
        utm_source: new URLSearchParams(window.location.search).get('utm_source') || '',
        utm_medium: new URLSearchParams(window.location.search).get('utm_medium') || '',
        utm_campaign: new URLSearchParams(window.location.search).get('utm_campaign') || '',
        // Timestamp
        timestamp: new Date().toISOString()
      };
      
      debug.log('Submitting subscriber with metadata', metadata);
      debug.log(`Email being submitted: ${email} (Type: ${typeof email})`);
      
      // Ensure email is trimmed
      const trimmedEmail = email.trim();
      debug.log('Trimmed email:', trimmedEmail);
      
      // Add new subscriber with enhanced metadata
      await addSubscriber(trimmedEmail, i18n.language, metadata);
      
      // Success state
      setStatus('success');
      setEmail('');
      
      // Track conversion in analytics
      trackSubscription('subscription_form');
    } catch (error) {
      console.error('Subscription error:', error);
      debug.error('Error adding subscriber', error);
      setStatus('error');
      
      if (error instanceof Error && error.message === 'Email already subscribed') {
        setErrorMessage(String(t('formElements.emailExistsError')));
      } else {
        setErrorMessage(String(t('formElements.generalError')));
      }
    }
  };
  
  return (
    <div className="w-full max-w-md">
      <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row w-full gap-2">
        <div className="flex-grow">
          <input
            type="email"
            value={email}
            onChange={handleEmailChange}
            placeholder={String(t('formElements.emailPlaceholder'))}
            className={`w-full px-4 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-800 bg-white ${status === 'error' ? 'border-red-500' : ''}`}
            required
            disabled={status === 'submitting'}
            aria-label={String(t('formElements.emailPlaceholder'))}
          />
        </div>

        <Button
          type="submit"
          disabled={status === 'submitting'}
          ariaLabel={buttonText || String(t('formElements.submitButton'))}
        >
          {status === 'submitting'
            ? t('formElements.submitting')
            : (buttonText || t('formElements.submitButton'))}
        </Button>
      </form>
      
      {status === 'success' && (
        <p className="mt-2 text-green-600 font-medium text-center" role="alert">
          {t('formElements.successMessage')}
        </p>
      )}
      
      {status === 'error' && (
        <p className="mt-2 text-red-600 font-medium text-center" role="alert">
          {errorMessage || t('formElements.generalError')}
        </p>
      )}
    </div>
  );
};

export default SubscriptionForm;